import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  PenTool,
  RotateCcw,
  Smartphone,
} from "lucide-react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MobileSignaturePage() {
  const { token } = useParams();
  const sigCanvasRef = useRef(null);

  const [tokenInfo, setTokenInfo] = useState(null);
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`)
      .then(r => r.json()).then(d => { if (d.shop_name) setShopName(d.shop_name); }).catch(() => {});
  }, []);

useEffect(() => {
    const fetchTokenInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/mobile-signature/${token}/info`);
        setTokenInfo(res.data);
        if (res.data.already_signed) {
          setSuccess(true);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Ce lien de signature n'est pas valide.");
        } else if (err.response?.status === 410) {
          setError("Ce lien de signature a expiré. Demandez un nouveau QR code au technicien.");
        } else {
          setError("Une erreur est survenue. Veuillez réessayer.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTokenInfo();
  }, [token]);

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSubmit = async () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      alert("Veuillez signer avant de valider.");
      return;
    }

    setSubmitting(true);
    try {
      const signatureData = sigCanvasRef.current.toDataURL("image/png");
      await axios.post(`${API_URL}/api/mobile-signature/${token}/submit`, {
        signature_data: signatureData,
      });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 410) {
        setError("Ce lien a expiré. Demandez un nouveau QR code au technicien.");
      } else {
        alert("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- LOADING ----
  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  // ---- ERROR ----
  if (error) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-red-500/30 bg-red-500/5">
          <CardContent className="pt-6 flex flex-col items-center gap-4 text-center">
            <AlertCircle className="w-12 h-12 text-red-400" />
            <div>
              <p className="font-semibold text-gray-900">Lien invalide</p>
              <p className="text-sm text-gray-500 mt-1">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- SUCCESS ----
  if (success) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="pt-6 flex flex-col items-center gap-4 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-400" />
            <div>
              <p className="text-xl font-bold text-gray-900">Signature enregistrée !</p>
              <p className="text-sm text-gray-500 mt-2">
                Merci {(tokenInfo?.client_name || "").split(" ")[0] || tokenInfo?.client_name}. Votre signature a bien été transmise au technicien.
              </p>
              <p className="text-xs text-gray-500 mt-3 bg-gray-100 rounded p-2">
                Vous pouvez fermer cette page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- SIGNATURE FORM ----
  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col items-center justify-start p-4 pt-8">
      <div className="w-full max-w-sm space-y-4">

        {/* Header */}
        <div className="flex flex-col items-center gap-2 text-center mb-2">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Signature de réception</h1>
          <p className="text-sm text-gray-500">{shopName}</p>
        </div>

        {/* Repair Info */}
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-4 pb-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Client</span>
              <span className="font-medium text-gray-900">{tokenInfo?.client_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Appareil</span>
              <span className="font-medium text-gray-900">{tokenInfo?.device_info}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">N° ticket</span>
              <span className="font-medium text-gray-900 font-mono text-xs">{tokenInfo?.ticket_number}</span>
            </div>
          </CardContent>
        </Card>

        {/* Signature Area */}
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PenTool className="w-4 h-4 text-primary" />
              Signez ici
            </CardTitle>
            <p className="text-xs text-gray-500">
              En signant, vous confirmez avoir récupéré votre appareil en bon état.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="border-2 border-dashed border-gray-200 rounded-lg overflow-hidden bg-white touch-none">
              <SignatureCanvas
                ref={sigCanvasRef}
                canvasProps={{
                  className: "w-full",
                  style: { width: "100%", height: "200px", display: "block" },
                  width: 400,
                  height: 200,
                }}
                backgroundColor="white"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              className="text-gray-500 w-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Effacer et recommencer
            </Button>
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Valider ma signature
            </>
          )}
        </Button>

        <p className="text-xs text-center text-gray-500 pb-4">
          Ce lien est valable 15 minutes et à usage unique.
        </p>
      </div>
    </div>
  );
}
